import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/NavigationSidebar';
import AgentStatusIndicator from '../../components/AgentStatusIndicator';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import ProgressTracker from './components/ProgressTracker';
import FormSection from './components/FormSection';
import NavigationButtons from './components/NavigationButtons';
import CompletionModal from './components/CompletionModal';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const OnboardingAgent = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [savingError, setSavingError] = useState(null);

  const steps = [
    {
      id: 'business-info',
      title: 'Business Information',
      icon: 'Building2',
      description: 'Tell us about your company to help our agents understand your business context.',
      helpText: 'This information helps all agents provide personalized assistance tailored to your industry and business model.',
      fields: [
        {
          id: 'companyName',
          type: 'text',
          label: 'Company Name',
          placeholder: 'Enter your company name',
          required: true,
          description: 'Legal or trading name of your business'
        },
        {
          id: 'industry',
          type: 'select',
          label: 'Industry Type',
          placeholder: 'Select your industry',
          required: true,
          description: 'Primary industry your business operates in',
          options: [
            { value: 'technology', label: 'Technology & Software' },
            { value: 'ecommerce', label: 'E-commerce & Retail' },
            { value: 'healthcare', label: 'Healthcare & Wellness' },
            { value: 'finance', label: 'Finance & Banking' },
            { value: 'education', label: 'Education & Training' },
            { value: 'realestate', label: 'Real Estate' },
            { value: 'hospitality', label: 'Hospitality & Tourism' },
            { value: 'manufacturing', label: 'Manufacturing' },
            { value: 'consulting', label: 'Consulting & Services' },
            { value: 'media', label: 'Media & Entertainment' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'companySize',
          type: 'select',
          label: 'Company Size',
          placeholder: 'Select company size',
          required: true,
          description: 'Number of employees in your organization',
          options: [
            { value: 'solo', label: 'Solo (1 employee)' },
            { value: 'small', label: 'Small (2-10 employees)' },
            { value: 'medium', label: 'Medium (11-50 employees)' },
            { value: 'large', label: 'Large (51-200 employees)' },
            { value: 'enterprise', label: 'Enterprise (200+ employees)' }
          ]
        },
        {
          id: 'website',
          type: 'url',
          label: 'Company Website',
          placeholder: 'https://www.example.com',
          required: false,
          description: 'Your primary business website URL'
        },
        {
          id: 'description',
          type: 'textarea',
          label: 'Business Description',
          placeholder: 'Describe what your company does, your products/services, and unique value proposition...',
          required: true,
          description: 'Brief overview of your business (50-200 words)'
        }
      ]
    },
    {
      id: 'target-audience',
      title: 'Target Audience',
      icon: 'Users',
      description: 'Help us understand who your customers are and what they need.',
      helpText: 'Detailed audience information enables Marketing Operations agent to create highly targeted campaigns.',
      fields: [
        {
          id: 'targetMarket',
          type: 'select',
          label: 'Primary Target Market',
          placeholder: 'Select target market',
          required: true,
          description: 'Who are your primary customers?',
          options: [
            { value: 'b2b', label: 'B2B (Business to Business)' },
            { value: 'b2c', label: 'B2C (Business to Consumer)' },
            { value: 'b2b2c', label: 'B2B2C (Hybrid Model)' }
          ]
        },
        {
          id: 'audienceAge',
          type: 'select',
          label: 'Primary Age Range',
          placeholder: 'Select age range',
          required: true,
          description: 'Main age demographic of your target audience',
          options: [
            { value: '18-24', label: '18-24 years' },
            { value: '25-34', label: '25-34 years' },
            { value: '35-44', label: '35-44 years' },
            { value: '45-54', label: '45-54 years' },
            { value: '55+', label: '55+ years' },
            { value: 'all', label: 'All ages' }
          ]
        },
        {
          id: 'audienceLocation',
          type: 'select',
          label: 'Geographic Focus',
          placeholder: 'Select primary location',
          required: true,
          description: 'Where are your customers located?',
          options: [
            { value: 'local', label: 'Local (City/Region)' },
            { value: 'national', label: 'National' },
            { value: 'international', label: 'International' },
            { value: 'global', label: 'Global' }
          ]
        },
        {
          id: 'customerPainPoints',
          type: 'textarea',
          label: 'Customer Pain Points',
          placeholder: 'What problems do your customers face that your business solves?',
          required: true,
          description: 'Key challenges your target audience experiences'
        }
      ]
    },
    {
      id: 'marketing-channels',
      title: 'Marketing Channels',
      icon: 'TrendingUp',
      description: 'Select your current and planned marketing channels.',
      helpText: 'This helps Marketing Operations agent focus on the right platforms and create channel-specific content.',
      fields: [
        {
          id: 'currentChannels',
          type: 'checkbox-group',
          label: 'Current Marketing Channels',
          required: true,
          description: 'Select all channels you currently use',
          options: [
            { value: 'email', label: 'Email Marketing', description: 'Newsletters and email campaigns' },
            { value: 'social', label: 'Social Media', description: 'Facebook, Instagram, LinkedIn, Twitter' },
            { value: 'content', label: 'Content Marketing', description: 'Blog posts, articles, guides' },
            { value: 'paid', label: 'Paid Advertising', description: 'Google Ads, Facebook Ads, etc.' },
            { value: 'seo', label: 'SEO', description: 'Search engine optimization' },
            { value: 'video', label: 'Video Marketing', description: 'YouTube, TikTok, video ads' },
            { value: 'events', label: 'Events & Webinars', description: 'Online and offline events' },
            { value: 'partnerships', label: 'Partnerships', description: 'Affiliate and influencer marketing' }
          ]
        },
        {
          id: 'marketingBudget',
          type: 'select',
          label: 'Monthly Marketing Budget',
          placeholder: 'Select budget range',
          required: true,
          description: 'Approximate monthly spend on marketing',
          options: [
            { value: 'under1k', label: 'Under $1,000' },
            { value: '1k-5k', label: '$1,000 - $5,000' },
            { value: '5k-10k', label: '$5,000 - $10,000' },
            { value: '10k-25k', label: '$10,000 - $25,000' },
            { value: '25k-50k', label: '$25,000 - $50,000' },
            { value: 'over50k', label: 'Over $50,000' }
          ]
        },
        {
          id: 'contentFrequency',
          type: 'select',
          label: 'Desired Content Frequency',
          placeholder: 'Select frequency',
          required: true,
          description: 'How often do you want to publish content?',
          options: [
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly (2-3 times)' },
            { value: 'biweekly', label: 'Bi-weekly' },
            { value: 'monthly', label: 'Monthly' }
          ]
        }
      ]
    },
    {
      id: 'growth-objectives',
      title: 'Growth Objectives',
      icon: 'Target',
      description: 'Define your business goals and success metrics.',
      helpText: 'Clear objectives help Feedback & Analytics agent track the right metrics and provide actionable insights.',
      fields: [
        {
          id: 'primaryGoal',
          type: 'select',
          label: 'Primary Business Goal',
          placeholder: 'Select primary goal',
          required: true,
          description: 'What is your main objective for the next 6 months?',
          options: [
            { value: 'awareness', label: 'Increase Brand Awareness' },
            { value: 'leads', label: 'Generate More Leads' },
            { value: 'sales', label: 'Increase Sales Revenue' },
            { value: 'retention', label: 'Improve Customer Retention' },
            { value: 'engagement', label: 'Boost Customer Engagement' },
            { value: 'market', label: 'Enter New Markets' }
          ]
        },
        {
          id: 'kpis',
          type: 'checkbox-group',
          label: 'Key Performance Indicators',
          required: true,
          description: 'Select metrics you want to track',
          options: [
            { value: 'traffic', label: 'Website Traffic', description: 'Visitors and page views' },
            { value: 'conversion', label: 'Conversion Rate', description: 'Lead to customer conversion' },
            { value: 'engagement', label: 'Engagement Rate', description: 'Social media and content engagement' },
            { value: 'roi', label: 'Marketing ROI', description: 'Return on marketing investment' },
            { value: 'retention', label: 'Customer Retention', description: 'Repeat customer rate' },
            { value: 'revenue', label: 'Revenue Growth', description: 'Monthly/quarterly revenue increase' }
          ]
        },
        {
          id: 'competitors',
          type: 'textarea',
          label: 'Main Competitors',
          placeholder: 'List 3-5 main competitors (company names or websites)',
          required: false,
          description: 'Helps Analytics agent perform competitive analysis'
        },
        {
          id: 'challenges',
          type: 'textarea',
          label: 'Current Challenges',
          placeholder: 'What are your biggest marketing or growth challenges?',
          required: true,
          description: 'Specific obstacles you want our agents to help overcome'
        }
      ]
    },
    {
      id: 'agent-preferences',
      title: 'Agent Preferences',
      icon: 'Settings',
      description: 'Customize how you want to work with your AI agents.',
      helpText: 'These preferences help all agents communicate and operate in a way that matches your workflow.',
      fields: [
        {
          id: 'communicationStyle',
          type: 'select',
          label: 'Preferred Communication Style',
          placeholder: 'Select style',
          required: true,
          description: 'How should agents communicate with you?',
          options: [
            { value: 'formal', label: 'Formal & Professional' },
            { value: 'casual', label: 'Casual & Friendly' },
            { value: 'concise', label: 'Concise & Direct' },
            { value: 'detailed', label: 'Detailed & Explanatory' }
          ]
        },
        {
          id: 'notificationFrequency',
          type: 'select',
          label: 'Notification Frequency',
          placeholder: 'Select frequency',
          required: true,
          description: 'How often should agents notify you?',
          options: [
            { value: 'realtime', label: 'Real-time (Immediate)' },
            { value: 'hourly', label: 'Hourly Digest' },
            { value: 'daily', label: 'Daily Summary' },
            { value: 'weekly', label: 'Weekly Report' }
          ]
        },
        {
          id: 'agentAutonomy',
          type: 'select',
          label: 'Agent Autonomy Level',
          placeholder: 'Select autonomy level',
          required: true,
          description: 'How much independence should agents have?',
          options: [
            { value: 'low', label: 'Low - Require approval for all actions' },
            { value: 'medium', label: 'Medium - Approve major decisions only' },
            { value: 'high', label: 'High - Agents work independently' }
          ]
        },
        {
          id: 'priorityTasks',
          type: 'checkbox-group',
          label: 'Priority Task Areas',
          required: true,
          description: 'What should agents focus on first?',
          options: [
            { value: 'campaigns', label: 'Email Campaigns', description: 'Create and optimize email marketing' },
            { value: 'content', label: 'Content Creation', description: 'Generate blog posts and social content' },
            { value: 'analytics', label: 'Analytics & Reporting', description: 'Track and analyze performance' },
            { value: 'productivity', label: 'Task Management', description: 'Organize daily activities' },
            { value: 'research', label: 'Market Research', description: 'Competitor and trend analysis' }
          ]
        },
        {
          id: 'additionalNotes',
          type: 'textarea',
          label: 'Additional Notes',
          placeholder: 'Any other information or special requirements for your agents?',
          required: false,
          description: 'Optional: Share any specific needs or preferences'
        }
      ]
    }
  ];

  const totalSteps = steps?.length;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const validateStep = (stepData) => {
    const newErrors = {};
    const currentStepData = steps?.[currentStep - 1];

    currentStepData?.fields?.forEach(field => {
      if (field?.required) {
        const value = formData?.[field?.id];
        if (!value || (Array.isArray(value) && value?.length === 0)) {
          newErrors[field.id] = `${field?.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const handleNext = async () => {
    if (validateStep()) {
      if (currentStep === totalSteps) {
        // Save data to Supabase before showing completion modal
        await saveOnboardingData();
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
    }
  };

  const saveOnboardingData = async () => {
    if (!user?.id) {
      setSavingError('User not authenticated');
      return;
    }

    setIsSaving(true);
    setSavingError(null);

    try {
      // Prepare data for user_profiles table
      const profileData = {
        company_name: formData?.companyName || null,
        industry: formData?.industry || null,
        company_size: formData?.companySize || null,
        website: formData?.website || null,
        business_description: formData?.description || null,
        target_market: formData?.targetMarket || null,
        audience_age: formData?.audienceAge || null,
        audience_location: formData?.audienceLocation || null,
        customer_pain_points: formData?.customerPainPoints || null,
        current_channels: formData?.currentChannels || [],
        marketing_budget: formData?.marketingBudget || null,
        content_frequency: formData?.contentFrequency || null,
        primary_goal: formData?.primaryGoal || null,
        kpis: formData?.kpis || [],
        competitors: formData?.competitors || null,
        challenges: formData?.challenges || null,
        communication_style: formData?.communicationStyle || null,
        notification_frequency: formData?.notificationFrequency || null,
        agent_autonomy: formData?.agentAutonomy || null,
        priority_tasks: formData?.priorityTasks || [],
        additional_notes: formData?.additionalNotes || null,
        onboarding_completed: true,
        onboarding_completed_at: new Date()?.toISOString()
      };

      // Update user_profiles table
      const { data, error } = await supabase?.from('user_profiles')?.update(profileData)?.eq('id', user?.id)?.select()?.single();

      if (error) {
        console.error('Error saving onboarding data:', error);
        setSavingError(error?.message || 'Failed to save onboarding data');
        setIsSaving(false);
        return;
      }

      console.log('Onboarding data saved successfully:', data);
      setShowCompletion(true);
    } catch (err) {
      console.error('Unexpected error saving onboarding data:', err);
      setSavingError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
    }, 1000);
  };

  const isStepValid = () => {
    const currentStepData = steps?.[currentStep - 1];
    return currentStepData?.fields?.every(field => {
      if (!field?.required) return true;
      const value = formData?.[field?.id];
      return value && (!Array.isArray(value) || value?.length > 0);
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar isCollapsed={isCollapsed} />
      <div className={`transition-all duration-250 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-60'} pb-20 lg:pb-0`}>
        <header className="sticky top-0 z-100 bg-card border-b border-border shadow-warm-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 md:gap-4">
                <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
                  aria-label="Toggle sidebar"
                >
                  <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                </button>
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground font-heading">
                    Onboarding Agent
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground font-caption mt-1">
                    Configure your AI agents for personalized assistance
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <AgentStatusIndicator 
                  status="active" 
                  agentName="Onboarding Agent"
                  size="default"
                  showLabel={false}
                  showIcon={true}
                />
                <TutorialAccessButton 
                  tutorialProgress={0} 
                  onTutorialStart={() => {}}
                />
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            <ProgressTracker 
              currentStep={currentStep}
              totalSteps={totalSteps}
              steps={steps}
            />

            <FormSection
              step={steps?.[currentStep - 1]}
              formData={formData}
              onChange={handleChange}
              errors={errors}
            />

            <NavigationButtons
              currentStep={currentStep}
              totalSteps={totalSteps}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onSave={handleSave}
              isValid={isStepValid()}
              isSaving={isSaving}
            />

            {savingError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Error Saving Data</p>
                  <p className="text-sm text-red-600 mt-1">{savingError}</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <CompletionModal
        isOpen={showCompletion}
        onClose={() => setShowCompletion(false)}
        companyName={formData?.companyName}
      />
    </div>
  );
};

export default OnboardingAgent;